import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import SetupPanel from '../AreaOverview/SetupPanel';

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const empty = () => ({ operation: 'start', days: [...DAYS], time: '09:00',
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', enabled: false, confirmTested: false });
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
export default function ScreenPowerSchedules({ areaId, screenId, screenName }) {
  const [data, setData] = useState({ schedules: [], occurrences: [] });
  const [draft, setDraft] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const mounted = useRef(false);
  const base = `/api/areas/${areaId}/topology/screens/${screenId}/power-schedules`;
  const load = useCallback(async () => {
    try {
      const response = await axios.get(base, { headers: headers(), timeout: 10000 });
      if (mounted.current) setData(response.data);
    } catch (error) { if (mounted.current) setMessage(error.response?.data?.message || error.message); }
  }, [base]);
  useEffect(() => {
    mounted.current = true;
    let timer;
    const refresh = async () => {
      await load();
      if (mounted.current) timer = setTimeout(refresh, 30000);
    };
    refresh();
    return () => { mounted.current = false; clearTimeout(timer); };
  }, [load]);
  const save = async (value) => {
    if (busy) return;
    setBusy(true); setMessage('');
    try {
      const response = await axios[value.id ? 'put' : 'post'](`${base}${value.id ? `/${value.id}` : ''}`, value, { headers: headers(), timeout: 15000 });
      if (!mounted.current) return;
      setData(response.data); setDraft(empty());
      setMessage(value.enabled ? 'Schedule saved and enabled. It will use the tested Screen routine.' : 'Schedule saved disabled. No future actions will be sent from this entry.');
    } catch (error) { if (mounted.current) setMessage(error.response?.data?.message || error.message); }
    finally { if (mounted.current) setBusy(false); }
  };
  const remove = async (entry) => {
    if (busy || !window.confirm(`Delete the ${entry.operation === 'start' ? 'Start' : 'Stop'} schedule at ${entry.time} (${entry.timeZone})? Its scheduled results will also be removed. Any Screen routine already running will continue.`)) return;
    setBusy(true); setMessage('');
    try {
      const response = await axios.delete(`${base}/${entry.id}`, { headers: headers(), timeout: 15000 });
      if (!mounted.current) return;
      setData(response.data);
      setDraft((current) => current.id === entry.id ? empty() : current);
      setMessage('Schedule deleted.');
    } catch (error) { if (mounted.current) setMessage(error.response?.data?.message || error.message); }
    finally { if (mounted.current) setBusy(false); }
  };
  return <SetupPanel title={`Scheduled power · ${screenName || 'Screen'}`} description="Weekly Start/Stop using this Screen’s saved routine — separate from content and device schedules">
    <section className="screen-power-schedules" aria-label="Screen power schedules">
      <p>Times use the zone you choose below. Missed actions can run up to five minutes late; older ones are skipped. Shared/site coordination comes later—stagger different Screens while the site runs one routine at a time.</p>
      <p>Do not overlap legacy device power schedules with these routines. Disabling an entry does not cancel an action already running.</p>
      <button type="button" onClick={load} disabled={busy}>Refresh schedules and results</button>
      <ul>{(data.schedules || []).map((entry) => <li key={entry.id}>
        <div><strong>{entry.operation === 'start' ? 'Start' : 'Stop'} · {entry.time} · {entry.enabled ? 'Enabled' : 'Disabled'}</strong>
          <small>{entry.days.join(', ')} · {entry.timeZone}</small></div>
        <button type="button" disabled={busy} onClick={() => { setDraft({ ...entry, confirmTested: false }); setMessage(''); }}>Edit / reconfirm</button>
        {entry.enabled && <button type="button" disabled={busy} onClick={() => save({ ...entry, enabled: false })}>Disable</button>}
        <button type="button" disabled={busy} onClick={() => remove(entry)}>Delete</button>
      </li>)}</ul>
      {!data.schedules?.length && <p>No scheduled power actions for this Screen yet.</p>}
      <form onSubmit={(event) => { event.preventDefault(); save(draft); }}>
        <h4>{draft.id ? 'Edit scheduled action' : 'Add a scheduled action'}</h4>
        <div className="screen-power-schedules__fields">
          <label>Action<select value={draft.operation} onChange={(event) => setDraft({ ...draft, operation: event.target.value, confirmTested: false })}><option value="start">Start Screen</option><option value="stop">Stop Screen</option></select></label>
          <label>Local time<input type="time" required value={draft.time} onChange={(event) => setDraft({ ...draft, time: event.target.value })} /></label>
          <label>Time zone<input required value={draft.timeZone} placeholder="Asia/Ho_Chi_Minh" onChange={(event) => setDraft({ ...draft, timeZone: event.target.value })} /><small>Defaults to your browser’s zone; check it matches the venue.</small></label>
        </div>
        <fieldset><legend>Days</legend>{DAYS.map((day) => <label key={day}><input type="checkbox" checked={draft.days.includes(day)} onChange={(event) => setDraft({ ...draft, days: event.target.checked ? [...draft.days, day] : draft.days.filter((value) => value !== day) })} />{day}</label>)}</fieldset>
        <label><input type="checkbox" checked={draft.enabled} onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })} />Enable this scheduled action</label>
        {draft.enabled && <label><input type="checkbox" required checked={draft.confirmTested} onChange={(event) => setDraft({ ...draft, confirmTested: event.target.checked })} />I have tested this Screen’s {draft.operation === 'start' ? 'startup' : 'shutdown'} routine, including its connected devices and retained wake relay.</label>}
        <small>Changing the Screen’s configuration blocks scheduled execution until you retest and reconfirm. Unknown power state never triggers a blind toggle.</small>
        <div><button type="submit" disabled={busy || !draft.days.length || (draft.enabled && !draft.confirmTested)}>{busy ? 'Saving…' : 'Save scheduled action'}</button>
          {draft.id && <button type="button" disabled={busy} onClick={() => setDraft(empty())}>Cancel edit</button>}</div>
      </form>
      {message && <p role="status">{message}</p>}
      <h4>Recent scheduled results</h4>
      <ul>{(data.occurrences || []).map((entry) => <li key={`${entry.scheduleId}:${entry.localDate}:${entry.revision}`}><div>
        <strong>{entry.operation === 'start' ? 'Start' : 'Stop'} · {new Date(entry.dueMs).toLocaleString(undefined, { timeZone: entry.timeZone })} ({entry.timeZone})</strong>
        <small>{entry.status.replaceAll('_', ' ')}{entry.operationId ? ` · ${entry.operationId}` : ''}</small>
        {entry.result?.message && <p>{entry.result.message}</p>}
        {(entry.result?.issues || []).map((issue, index) => <p key={index}>{issue.message}</p>)}
        {(entry.result?.warnings || []).map((warning, index) => <p key={index}>{warning}</p>)}
      </div></li>)}</ul>
      {!data.occurrences?.length && <p>No scheduled runs recorded yet.</p>}
    </section>
  </SetupPanel>;
}
